import * as React from 'react';
import { styled, css } from '../theme-provider';
import { CharacterCounter } from '../character-counter';
import { DisplayNotification, InputAppearance, getAuxillaryColor, getAuxillaryTextColor, getMainColor, getLabelColor, } from '../helpers/input';
import { getCommonProps } from '../helpers/common-props';
const StyledLabelText = styled.span.withConfig({ displayName: "StyledLabelText", componentId: "sc-oyarl6" }) `font-size:${({ theme }) => theme.fonts.label[100].fontSize};display:block;pointer-events:none;position:absolute;margin:0;padding:0;top:0;color:transparent;`;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- StyledLabelText has the right type, this just prevents some TS type inference issue
const StyledLabelTextWithFloating = styled(StyledLabelText).withConfig({ displayName: "StyledLabelTextWithFloating", componentId: "sc-bjhvx3" }) `font-size:${({ theme, isFloating }) => isFloating
    ? theme.fonts.copy[200].fontSize
    : theme.fonts.label[100].fontSize};transition:${(props) => `color ${props.theme.animation.timingFunction.cubicOut} ${props.theme.animation.duration.color}`};position:absolute;top:0;color:inherit;${({ theme, isFloating, hasValue, labelHeight }) => isFloating && !hasValue
    ? css `
					width: 100%;
					white-space: nowrap;
					text-overflow: ellipsis;
					overflow: hidden;
					padding-right: ${theme.size.static515};
					transition: all ease-in-out 0.25s;
					transform: translateY(${labelHeight}px)
						translateY(${theme.fonts.label[100].fontSize});
				`
    : undefined}`;
const StyledTextArea = styled.textarea.withConfig({ displayName: "StyledTextArea", componentId: "sc-1p9sgje" }) `width:100%;border-radius:0;min-height:${({ theme }) => `calc(${theme.size.static150} * 2 + ${theme.fonts.copy[200].lineHeight})`};background:transparent;border:0;color:${getMainColor};cursor:${({ appearance }) => appearance === InputAppearance.Disabled ? 'not-allowed' : ''};padding:${(props) => props.theme.size.static150} 0;outline:0;font-size:${({ theme }) => theme.fonts.copy[200].fontSize};line-height:${({ theme }) => theme.fonts.copy[200].lineHeight};font-family:inherit;margin-top:calc(\n\t\t${(props) => props.labelHeight + `px`} -\n\t\t\t${(props) => props.theme.size.static250}\n\t);box-shadow:0 ${({ theme }) => theme.size.line.default} 0 0 ${getAuxillaryColor};${({ appearance }) => appearance === InputAppearance.ReadOnly && 'box-shadow: none;'} &::placeholder{opacity:1;color:${(props) => props.theme.colors.content.secondary};}${({ theme, hasValue }) => hasValue &&
    css `
			& ~ ${StyledLabelTextWithFloating} {
				transform: translateY(0);
				font-size: ${theme.fonts.label[100].fontSize};
			}
		`} ${({ appearance }) => appearance === InputAppearance.Success ||
    appearance === InputAppearance.Error ||
    appearance === InputAppearance.Information
    ? css `
					:hover:not(:read-only) {
						box-shadow: 0 ${({ theme }) => theme.size.line.default} 0 0
							${getAuxillaryColor};
					}

					:focus:not(:read-only) {
						box-shadow: 0 ${({ theme }) => theme.size.line.active} 0 0
							${getAuxillaryColor};
					}
				`
    : css `
					:hover:not(:read-only) {
						transition: box-shadow 300ms ease;
						-webkit-transition: box-shadow 300ms ease;
						box-shadow: 0 ${({ theme }) => theme.size.line.default} 0 0
							${({ theme }) => theme.colors.border.hover};
					}
				`}:focus:not(:read-only){${({ theme, appearance }) => appearance === InputAppearance.Default &&
    `border-bottom-color: ${theme.colors.content.active};`} margin-bottom:0;box-shadow:0 ${({ theme }) => theme.size.line.active} 0 0 ${({ theme }) => theme.colors.focus.main};& + div span{color:${(props) => props.theme.colors.content.active};}~ ${StyledLabelTextWithFloating}{transform:translateY(0);font-size:${({ theme }) => theme.fonts.label[100].fontSize};}}`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-3rzts0" }) `display:block;width:100%;position:relative;padding-top:${(props) => props.theme.size.static250};color:${getLabelColor};`;
const StyledAdditionalInfo = styled.div.withConfig({ displayName: "StyledAdditionalInfo", componentId: "sc-61bvzt" }) `display:flex;flex-direction:row;padding-top:${({ theme }) => theme.size.static100};color:${getAuxillaryTextColor};`;
export const TextArea = (props) => {
    const labelRef = React.createRef();
    const { label, appearance = InputAppearance.Default, name, onBlur, onChange, onFocus, value, defaultValue, placeholder, rows = 1, minLength, maxLength, required, isFloating, message, inputMode, characterCountLimit = maxLength, customId, } = props;
    const commonProps = getCommonProps(props);
    const [characterCount, setCharacterCount] = React.useState((value === null || value === void 0 ? void 0 : value.length) || (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.length) || 0);
    const [hasValue, setHasValue] = React.useState(false);
    const [labelHeight, setLabelHeight] = React.useState(0);
    React.useEffect(() => {
        setCharacterCount((value === null || value === void 0 ? void 0 : value.length) || 0);
    }, [value]);
    React.useEffect(() => {
        var _a;
        setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0);
    }, [labelRef]);
    const handleResize = React.useCallback(() => { var _a; return setLabelHeight(((_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0); }, [setLabelHeight, labelRef]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const handleChange = React.useCallback((event) => {
        const currentValueLenght = event.currentTarget.value.length;
        setCharacterCount(currentValueLenght);
        setHasValue(currentValueLenght > 0);
        if (onChange) {
            onChange(event);
        }
    }, [onChange]);
    /*
    ariaInvalid and ariaDescribedBy are used to set the aria-invalid and aria-describedby attributes
    according to the appearance of the input. These properties are needed to make the input and the DisplayNotification component accessible.
    */
    const ariaInvalid = InputAppearance[appearance] === 'Error';
    const ariaDescribedBy = {
        Error: `error-message${customId}`,
        Success: `success-message${customId}`,
        Information: `information-message${customId}`,
    }[InputAppearance[appearance]];
    return (React.createElement(StyledLabel, Object.assign({ appearance: appearance }, commonProps),
        label && (React.createElement(StyledLabelText, { "aria-hidden": true, ref: labelRef },
            label,
            required && '*')),
        React.createElement(StyledTextArea, { readOnly: appearance === InputAppearance.ReadOnly, disabled: appearance === InputAppearance.Disabled, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, rows: rows, appearance: appearance, minLength: minLength, maxLength: maxLength, name: name, onChange: handleChange, onBlur: onBlur, onFocus: onFocus, placeholder: placeholder, value: value, defaultValue: defaultValue, required: required, "aria-required": required, hasValue: characterCount > 0, inputMode: inputMode, labelHeight: labelHeight }),
        label && (React.createElement(StyledLabelTextWithFloating, { isFloating: isFloating, hasValue: hasValue, labelHeight: labelHeight },
            label,
            required && '*')),
        React.createElement(StyledAdditionalInfo, { appearance: appearance },
            message && (React.createElement(DisplayNotification, { appearance: appearance, notificationMessage: message, id: ariaDescribedBy })),
            characterCountLimit && (React.createElement(CharacterCounter, { value: characterCount, limit: characterCountLimit })))));
};
